/**
 * Per-route document titles — so browser tabs stop all reading "Flat Hero".
 * Brand-FIRST format (Piotr 2026-07-20): typing "flathero" in the browser tab
 * switcher finds every app tab; typing "ops" filters the console ones.
 *  - /admin/* + /analytics: internal console, Polish-only by design →
 *    literal "Flathero.pl Ops: <Sekcja>".
 *  - public app: i18n keys (Convention 2) → "Flathero.pl: <t(key)>".
 * Longest-prefix wins; unknown routes fall back to the bare brand.
 * SEO note: SERP best practice is page-name-first with brand suffix — that is
 * handled by edge meta-injection for bot-served HTML (docs/SeoAndLinkPreviews.md
 * E1), so tab titles can stay brand-first for findability.
 */

export type PageTitleSpec =
  | { literal: string }
  | { key: string }
  | null;

const ADMIN_TITLES: [string, string][] = [
  ['/admin/users', 'Użytkownicy'],
  ['/admin/listings', 'Ogłoszenia'],
  ['/admin/spiecie', 'Spięcia'],
  ['/admin/inbox', 'Inbox'],
  ['/admin/leads', 'Leady'],
  ['/admin/ai-costs', 'Koszty AI'],
  ['/admin/conversations', 'Rozmowy'],
  ['/admin/reports', 'Zgłoszenia'],
  ['/admin/analytics', 'Analityka'],
  ['/admin/settings', 'Ustawienia'],
];

const APP_TITLES: [string, string][] = [
  ['/search', 'titles.search'],
  ['/offer', 'titles.offer'],
  ['/messages', 'titles.messages'],
  ['/notifications', 'titles.notifications'],
  ['/profile', 'titles.profile'],
  ['/settings', 'titles.settings'],
  ['/community', 'titles.community'],
  ['/owner/wizard', 'titles.owner_wizard'],
  ['/owner/listing/edit', 'titles.owner_edit'],
  ['/owner/claim', 'titles.claim'],
  ['/owner/calendar', 'titles.owner_calendar'],
  ['/owner', 'titles.owner'],
  ['/onboarding', 'titles.onboarding'],
  ['/login', 'titles.login'],
  ['/signup', 'titles.signup'],
  ['/claim', 'titles.claim'],
  ['/wlasciciel', 'titles.claim'],
  ['/dla-ciebie', 'titles.for_you'],
];

/** Environment tag for tab titles — internal envs only, PRODUCTION stays clean
 *  (users see tab titles; the tag is our internal noise). Searchable in the tab
 *  switcher: typing "piotr" finds that env's tabs. */
export function envTagForHost(hostname: string, devWorktree: string | null = null): string | null {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) {
    // Local dev: name the AGENT whose worktree launched this server
    // ("flat-hero-owner" -> "owner"); bare main copy -> plain "dev".
    const agent = (devWorktree ?? '').replace(/^flat-hero-?/, '').trim();
    return agent !== '' ? agent : 'dev';
  }
  const m = h.match(/^(piotr|maciej|bartek|stage)\./);
  return m ? m[1] : null;
}

/** "[piotr] Tytuł" on internal envs; bare title on production. */
export function withEnvTag(title: string, hostname: string, devWorktree: string | null = null): string {
  const tag = envTagForHost(hostname, devWorktree);
  return tag ? `[${tag}] ${title}` : title;
}

export function pageTitleSpec(pathname: string): PageTitleSpec {
  if (pathname.startsWith('/admin') || pathname === '/analytics') {
    if (pathname === '/analytics') return { literal: 'Flathero.pl Ops: Analityka' };
    const hit = ADMIN_TITLES.find(([prefix]) => pathname.startsWith(prefix));
    return { literal: hit ? `Flathero.pl Ops: ${hit[1]}` : 'Flathero.pl Ops' };
  }
  const hit = APP_TITLES.find(([prefix]) => pathname.startsWith(prefix));
  return hit ? { key: hit[1] } : null;
}
